import { SignOut } from "@phosphor-icons/react";
import {
    ChevronUp,
    House,
    Languages,
    Newspaper,
    Radio,
    Rows4,
    Swords,
    Trophy,
    User2,
    UserPen,
    Users2,
    Video,
    Wrench,
} from "lucide-react";
import { ReactNode, useCallback, useMemo } from "react";
import { NavLink, useMatch } from "react-router-dom";
import { toast } from "sonner";

import { Protect } from "@/components/Protect";
import { Badge } from "@/components/ui/badge";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuSeparator,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarRail,
    useSidebar,
} from "@/components/ui/sidebar";
import { useAuth } from "@/hooks/useAuth";
import { Permissions } from "@/hooks/usePermissions";
import { useTroubleshootingStats } from "@/hooks/useTroubleshootingStats";
import { LanguageCode, supportedLanguages } from "@/utils/i18n";
import { useLanguageCodeWithDefault, useSessionLanguageCode } from "@/utils/language";

interface SidebarMenuLinkProps extends React.HTMLAttributes<HTMLAnchorElement> {
    to: string;
    icon: ReactNode;
    badge?: number;
}
function SidebarMenuLink({ to, icon, children, badge }: SidebarMenuLinkProps) {
    const isActive = useMatch({ path: to, end: false }) !== null;

    const { setOpenMobile } = useSidebar();
    const closeSidebar = useCallback(() => setOpenMobile(false), [setOpenMobile]);

    return (
        <SidebarMenuButton isActive={isActive} asChild>
            <NavLink to={to} onClick={closeSidebar}>
                {icon}
                <span>{children}</span>
                {badge !== undefined && badge > 0 && (
                    <Badge variant="destructive" className="ml-auto h-5 px-1.5 text-xs">
                        {badge}
                    </Badge>
                )}
            </NavLink>
        </SidebarMenuButton>
    );
}

export function AppSidebar() {
    const { authUser, signOut } = useAuth();
    const { setOpenMobile } = useSidebar();
    const closeSidebar = useCallback(() => setOpenMobile(false), [setOpenMobile]);
    const { totalIssues } = useTroubleshootingStats();

    const [_sessionLanguageCode, setSessionLanguageCode] = useSessionLanguageCode();
    const languageCode = useLanguageCodeWithDefault();
    const selectedLanguage = useMemo(
        () => supportedLanguages.find(lang => lang.code === languageCode),
        [languageCode]
    );

    return (
        <Sidebar collapsible="icon">
            <SidebarContent>
                <SidebarGroup>
                    <SidebarGroupLabel>Chess</SidebarGroupLabel>
                    <SidebarGroupContent>
                        <SidebarMenu>
                            <SidebarMenuItem>
                                <SidebarMenuLink to="/tournaments" icon={<Trophy />}>
                                    Tournaments
                                </SidebarMenuLink>
                            </SidebarMenuItem>
                            <SidebarMenuItem>
                                <SidebarMenuLink to="/tournament-series" icon={<Rows4 />}>
                                    Tournament series
                                </SidebarMenuLink>
                            </SidebarMenuItem>
                            <SidebarMenuItem>
                                <SidebarMenuLink to="/games" icon={<Swords />}>
                                    Live coverage
                                </SidebarMenuLink>
                            </SidebarMenuItem>
                        </SidebarMenu>
                    </SidebarGroupContent>
                </SidebarGroup>
                <SidebarGroup>
                    <SidebarGroupLabel>Editorial</SidebarGroupLabel>
                    <SidebarGroupContent>
                        <SidebarMenu>
                            <SidebarMenuItem>
                                <SidebarMenuLink to="/home-layouts" icon={<House />}>
                                    Home layouts
                                </SidebarMenuLink>
                            </SidebarMenuItem>
                            <SidebarMenuItem>
                                <SidebarMenuLink to="/tournament-layout-templates" icon={<House />}>
                                    Tournament Layout Templates
                                </SidebarMenuLink>
                            </SidebarMenuItem>
                            <SidebarMenuItem>
                                <SidebarMenuLink to="/articles" icon={<Newspaper />}>
                                    Articles
                                </SidebarMenuLink>
                            </SidebarMenuItem>
                            <SidebarMenuItem>
                                <SidebarMenuLink to="/streaming-channels" icon={<Radio />}>
                                    Streaming Channels
                                </SidebarMenuLink>
                            </SidebarMenuItem>
                        </SidebarMenu>
                    </SidebarGroupContent>
                </SidebarGroup>
                <SidebarGroup>
                    <SidebarGroupContent>
                        <SidebarMenu>
                            <Protect permission={Permissions.PLAYER_MANAGEMENT}>
                                <SidebarMenuItem>
                                    <SidebarMenuLink to="/players" icon={<Users2 />}>
                                        Players
                                    </SidebarMenuLink>
                                </SidebarMenuItem>
                            </Protect>
                            <SidebarMenuItem>
                                <SidebarMenuLink to="/videos" icon={<Video />}>
                                    Videos
                                </SidebarMenuLink>
                            </SidebarMenuItem>
                        </SidebarMenu>
                    </SidebarGroupContent>
                </SidebarGroup>
                <SidebarGroup>
                    <SidebarGroupLabel>Admin</SidebarGroupLabel>
                    <SidebarGroupContent>
                        <SidebarMenu>
                            <SidebarMenuItem>
                                <SidebarMenuLink
                                    to="/troubleshooting"
                                    icon={<Wrench />}
                                    badge={totalIssues}
                                >
                                    Troubleshooting
                                </SidebarMenuLink>
                            </SidebarMenuItem>
                        </SidebarMenu>
                    </SidebarGroupContent>
                </SidebarGroup>
            </SidebarContent>
            {authUser && (
                <SidebarFooter>
                    <SidebarMenu>
                        <SidebarMenuItem>
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <SidebarMenuButton>
                                        <User2 /> {authUser.displayName ?? authUser.email}
                                        <ChevronUp className="ml-auto" />
                                    </SidebarMenuButton>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                    side="top"
                                    className="w-[--radix-popper-anchor-width]"
                                >
                                    <DropdownMenuItem asChild>
                                        <NavLink to="/settings" onClick={closeSidebar}>
                                            <UserPen />
                                            Commentator profile
                                        </NavLink>
                                    </DropdownMenuItem>
                                    <DropdownMenuSub>
                                        <DropdownMenuSubTrigger>
                                            <Languages />
                                            Default language
                                        </DropdownMenuSubTrigger>
                                        <DropdownMenuSubContent>
                                            <DropdownMenuRadioGroup
                                                value={selectedLanguage?.code}
                                                onValueChange={value => {
                                                    setSessionLanguageCode(value as LanguageCode);
                                                    closeSidebar();
                                                }}
                                            >
                                                {supportedLanguages.map(language => (
                                                    <DropdownMenuRadioItem
                                                        key={language.code}
                                                        value={language.code}
                                                    >
                                                        {language.name}
                                                    </DropdownMenuRadioItem>
                                                ))}
                                            </DropdownMenuRadioGroup>
                                        </DropdownMenuSubContent>
                                    </DropdownMenuSub>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                        onClick={async () => {
                                            await signOut();
                                            toast.success("You were successfully signed out.");
                                        }}
                                    >
                                        <SignOut />
                                        <span>Sign out</span>
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </SidebarMenuItem>
                    </SidebarMenu>
                </SidebarFooter>
            )}
            <SidebarRail />
        </Sidebar>
    );
}
